// <emma-chat-dot-grid> — background canvas dot animation.
//
// 1:1 port of the monolith `initDotGrid` IIFE (static/index.html:4651-4862).
// Behaviors:
//   1. Static grid of dots at SPACING px, gray-15 base color, BASE_A alpha.
//   2. Pulse waves — emitted every PULSE_INTERVAL ms (constant), expand
//      outward at PULSE_SPEED px/s, ring of PULSE_RING px width brightens
//      dots and tints them with a cyan→blue→purple gradient based on
//      angle from the pulse center.
//   3. Mouse-proximity highlight — desktop only (no-op on coarse pointer),
//      MOUSE_R px radius, dots scale 2× and tint blue.
//   4. Dead zone — when `dead-zone` selector resolves to a child element of
//      the host's parent, dots inside its rect (+ DEAD_PAD) are skipped so
//      they don't paint behind the prompt-card / conversation.
//
// Performance:
//   - Single RAF loop drives all animation
//   - IntersectionObserver pauses RAF when off-viewport
//   - prefers-reduced-motion gate skips the RAF loop entirely (static empty
//     canvas — content survives without the decoration)
//
// Cleanup: cancel RAF, disconnect observer, remove resize / mouse listeners
// on disconnectedCallback — no leaks across mount/unmount cycles.

import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { designTokens } from './design-tokens.js';

interface Pulse {
  cx: number;
  cy: number;
  t0: number;
  maxR: number;
}

// Monolith constants (index.html :4655-4676).
const SPACING = 34;
const BASE_R = 1;
const BASE_A = 0.17;
const DOT_RGB: [number, number, number] = [186, 190, 204]; // ~gray-15

const PULSE_INTERVAL = 6000; // ms between spawns
const PULSE_SPEED = 200; // px/s wave expansion
const PULSE_RING = 250; // px ring width
const PULSE_SCALE = 1.8;
const PULSE_ALPHA = 0.45;

// 3-stop gradient: cyan → blue → purple. Maps angle 0..1 from the pulse
// centre to a color. Matches the thinking-state border gradient.
const GRAD: [number, number, number][] = [
  [118, 209, 254], // cyan
  [48, 88, 248], // blue
  [137, 56, 250], // purple
];

const MOUSE_R = 120;
const MOUSE_SCALE = 2.0;
const MOUSE_ALPHA = 0.5;
const MOUSE_RGB: [number, number, number] = [56, 94, 237]; // ~blue-100

const DEAD_PAD = 20;

@customElement('emma-chat-dot-grid')
export class EmmaChatDotGrid extends LitElement {
  /** When false, the RAF loop is not started. */
  @property({ type: Boolean })
  enabled = true;

  /** CSS selector (resolved against host's parent) for the content rect
   *  whose bounds should be excluded from the dot field. Set to e.g.
   *  ".hero" so dots don't paint behind the conversation. */
  @property({ type: String, attribute: 'dead-zone' })
  deadZone = '';

  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private rafId: number | null = null;
  private observer: IntersectionObserver | null = null;
  private inViewport = true;
  private mx = -9999;
  private my = -9999;
  private dots: { x: number; y: number }[] = [];
  private pulses: Pulse[] = [];
  private lastPulse = 0;
  private isCoarsePointer = false;
  private resizeRaf: number | null = null;
  private deadRect = { x: 0, y: 0, x2: 0, y2: 0 };
  private prefersReducedMotion = false;

  static override styles = [
    designTokens,
    css`
      :host {
        display: block;
        position: absolute;
        inset: 0;
        pointer-events: none;
        z-index: 0;
      }
      canvas {
        display: block;
        width: 100%;
        height: 100%;
      }
    `,
  ];

  override firstUpdated(): void {
    this.canvas = this.renderRoot.querySelector('canvas');
    if (!this.canvas) return;
    const ctx = this.canvas.getContext('2d');
    if (!ctx) return;
    this.ctx = ctx;

    this.isCoarsePointer = window.matchMedia('(pointer: coarse)').matches;
    this.prefersReducedMotion =
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.resize();
    window.addEventListener('resize', this.handleResize);

    if (!this.isCoarsePointer) {
      // Listen on the host's parent so coordinates align with our canvas
      // — using document is fine too, just more event noise.
      document.addEventListener('mousemove', this.handleMouseMove);
      document.addEventListener('mouseleave', this.handleMouseLeave);
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        this.inViewport = entry.isIntersecting;
        if (this.inViewport && this.enabled && !this.prefersReducedMotion) {
          this.startRaf();
        } else {
          this.stopRaf();
        }
      },
      { threshold: 0 },
    );
    this.observer.observe(this);

    // Reduced-motion: skip the RAF entirely. Canvas stays empty — page
    // chrome looks bare but motion is fully respected.
    if (this.enabled && !this.prefersReducedMotion) this.startRaf();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.stopRaf();
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.resizeRaf !== null) {
      cancelAnimationFrame(this.resizeRaf);
      this.resizeRaf = null;
    }
    window.removeEventListener('resize', this.handleResize);
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseleave', this.handleMouseLeave);
  }

  override updated(changed: Map<string, unknown>): void {
    if (changed.has('enabled')) {
      if (this.enabled && this.inViewport && !this.prefersReducedMotion) {
        this.startRaf();
      } else {
        this.stopRaf();
      }
    }
    if (changed.has('deadZone')) this.updateDeadZone();
  }

  private handleResize = (): void => {
    if (this.resizeRaf !== null) return;
    this.resizeRaf = requestAnimationFrame(() => {
      this.resizeRaf = null;
      this.resize();
    });
  };

  private handleMouseMove = (e: MouseEvent): void => {
    if (!this.canvas) return;
    const rect = this.canvas.getBoundingClientRect();
    this.mx = e.clientX - rect.left;
    this.my = e.clientY - rect.top;
  };

  private handleMouseLeave = (): void => {
    this.mx = -9999;
    this.my = -9999;
  };

  /** Set canvas backing-store size to viewport × DPR, then build the
   *  static grid coordinate cache. Called on init + resize. */
  private resize(): void {
    if (!this.canvas || !this.ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = this.canvas.getBoundingClientRect();
    const w = rect.width;
    const h = rect.height;
    this.canvas.width = w * dpr;
    this.canvas.height = h * dpr;
    this.canvas.style.width = w + 'px';
    this.canvas.style.height = h + 'px';
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.buildGrid(w, h);
    this.updateDeadZone();
  }

  /** Pre-compute dot positions. Centred so the grid is symmetric on
   *  both axes. */
  private buildGrid(w: number, h: number): void {
    this.dots = [];
    const cols = Math.ceil(w / SPACING) + 1;
    const rows = Math.ceil(h / SPACING) + 1;
    const ox = (w - (cols - 1) * SPACING) / 2;
    const oy = (h - (rows - 1) * SPACING) / 2;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        this.dots.push({ x: ox + c * SPACING, y: oy + r * SPACING });
      }
    }
  }

  /** Compute the dead-zone rect (relative to canvas) when `deadZone`
   *  selector is set. Looks up the element in the host's parent — the
   *  same scope where the dot-grid is mounted. Cleared (zero rect)
   *  when no selector or element not found. */
  private updateDeadZone(): void {
    if (!this.canvas || !this.deadZone) {
      this.deadRect = { x: 0, y: 0, x2: 0, y2: 0 };
      return;
    }
    const parent = this.parentElement;
    if (!parent) return;
    const target = parent.querySelector(this.deadZone) as HTMLElement | null;
    if (!target) {
      this.deadRect = { x: 0, y: 0, x2: 0, y2: 0 };
      return;
    }
    const cr = this.canvas.getBoundingClientRect();
    const tr = target.getBoundingClientRect();
    this.deadRect = {
      x: tr.left - cr.left - DEAD_PAD,
      y: tr.top - cr.top - DEAD_PAD,
      x2: tr.right - cr.left + DEAD_PAD,
      y2: tr.bottom - cr.top + DEAD_PAD,
    };
  }

  private startRaf(): void {
    if (this.rafId !== null) return;
    this.lastPulse = 0;
    const tick = (now: number): void => {
      this.draw(now);
      this.rafId = requestAnimationFrame(tick);
    };
    this.rafId = requestAnimationFrame(tick);
  }

  private stopRaf(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  private spawnPulse(now: number, w: number, h: number): void {
    this.pulses.push({
      cx: w * (0.3 + Math.random() * 0.4),
      cy: h * (0.3 + Math.random() * 0.4),
      t0: now,
      maxR: Math.max(w, h) * 1.2,
    });
    this.lastPulse = now;
  }

  /** Recompute the dead-zone every few hundred ms — the conversation
   *  card grows / shrinks as bubbles arrive, so a static measurement
   *  taken at mount time goes stale. Cheap (one getBoundingClientRect). */
  private maybeRefreshDeadZone(now: number): void {
    if ((now | 0) % 16 < 2) this.updateDeadZone();
  }

  private draw(now: number): void {
    if (!this.canvas || !this.ctx) return;
    const ctx = this.ctx;
    const rect = this.canvas.getBoundingClientRect();
    const w = rect.width;
    const h = rect.height;

    if (now - this.lastPulse > PULSE_INTERVAL) this.spawnPulse(now, w, h);
    this.maybeRefreshDeadZone(now);

    ctx.clearRect(0, 0, w, h);

    const _pulse = { t: 0, angle: 0 };
    const dz = this.deadRect;
    const dzActive = dz.x !== dz.x2;

    for (let i = 0; i < this.dots.length; i++) {
      const d = this.dots[i]!;
      if (
        dzActive &&
        d.x > dz.x &&
        d.x < dz.x2 &&
        d.y > dz.y &&
        d.y < dz.y2
      ) {
        continue;
      }

      let scale = 1;
      let alpha = BASE_A;
      let cr = DOT_RGB[0];
      let cg = DOT_RGB[1];
      let cb = DOT_RGB[2];

      // --- Pulse wave influence ---
      pulseInfluence(this.pulses, d, now, _pulse);
      if (_pulse.t > 0) {
        scale += (PULSE_SCALE - 1) * _pulse.t;
        alpha += (PULSE_ALPHA - BASE_A) * _pulse.t;
        const gc = gradColor(_pulse.angle);
        cr = Math.round(cr + (gc[0] - cr) * _pulse.t);
        cg = Math.round(cg + (gc[1] - cg) * _pulse.t);
        cb = Math.round(cb + (gc[2] - cb) * _pulse.t);
      }

      // --- Mouse-proximity highlight ---
      if (!this.isCoarsePointer && this.mx > -1000) {
        const dx = d.x - this.mx;
        const dy = d.y - this.my;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < MOUSE_R) {
          const mt = 1 - dist / MOUSE_R;
          const me = mt * mt;
          scale = Math.max(scale, 1 + (MOUSE_SCALE - 1) * me);
          alpha = Math.max(alpha, BASE_A + (MOUSE_ALPHA - BASE_A) * me);
          cr = Math.round(cr + (MOUSE_RGB[0] - cr) * me);
          cg = Math.round(cg + (MOUSE_RGB[1] - cg) * me);
          cb = Math.round(cb + (MOUSE_RGB[2] - cb) * me);
        }
      }

      ctx.beginPath();
      ctx.arc(d.x, d.y, BASE_R * scale, 0, 6.2832);
      ctx.fillStyle = `rgba(${cr},${cg},${cb},${alpha})`;
      ctx.fill();
    }

    // Drop expired pulses (the wave has expanded past its max radius
    // plus the ring width). Cheap O(n) compaction in-place.
    for (let i = this.pulses.length - 1; i >= 0; i--) {
      const p = this.pulses[i]!;
      const elapsed = (now - p.t0) / 1000;
      if (elapsed * PULSE_SPEED - PULSE_RING > p.maxR) {
        this.pulses.splice(i, 1);
      }
    }
  }

  override render() {
    return html`<canvas></canvas>`;
  }
}

/** Compute pulse influence at a dot — strongest when on the wave's
 *  expanding ring. Returns intensity (0..1) and angle (0..1) from the
 *  pulse's centre to feed the gradient lookup. Mutates `out` in place
 *  to avoid per-frame allocations. */
function pulseInfluence(
  pulses: Pulse[],
  dot: { x: number; y: number },
  now: number,
  out: { t: number; angle: number },
): void {
  let best = 0;
  let bestAngle = 0;
  for (const p of pulses) {
    const elapsed = (now - p.t0) / 1000;
    const waveR = elapsed * PULSE_SPEED;
    const dist = Math.hypot(dot.x - p.cx, dot.y - p.cy);
    const delta = Math.abs(dist - waveR);
    if (delta < PULSE_RING) {
      const t = 1 - delta / PULSE_RING;
      const tt = t * t;
      if (tt > best) {
        best = tt;
        bestAngle = (Math.atan2(dot.y - p.cy, dot.x - p.cx) / 6.2832 + 1) % 1;
      }
    }
  }
  out.t = best;
  out.angle = bestAngle;
}

/** Interpolate the 3-stop GRAD by `pos` (0..1). pos=0 → cyan, pos=0.5 →
 *  blue, pos=1 → purple. Returns RGB tuple. */
function gradColor(pos: number): [number, number, number] {
  const idx = pos * 2;
  const i0 = Math.min(Math.floor(idx), 1);
  const frac = idx - i0;
  const a = GRAD[i0]!;
  const b = GRAD[i0 + 1]!;
  return [
    Math.round(a[0] + (b[0] - a[0]) * frac),
    Math.round(a[1] + (b[1] - a[1]) * frac),
    Math.round(a[2] + (b[2] - a[2]) * frac),
  ];
}

declare global {
  interface HTMLElementTagNameMap {
    'emma-chat-dot-grid': EmmaChatDotGrid;
  }
}
